<template>
  <AppLayout title="Downloads & Materialien">
    <div class="min-h-screen bg-gray-50 py-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header Section -->
        <div class="text-center mb-12">
          <h1 class="text-3xl font-bold text-gray-900 mb-4">{{ $t('Downloads & Materialien') }}</h1>
          <p class="text-lg text-gray-600">{{ $t('Produktbilder, Anleitungen und weitere Ressourcen') }}</p>
        </div>

        <!-- Breadcrumb Navigation -->
        <div class="bg-white rounded-lg shadow-sm p-4 mb-8">
          <nav class="flex" aria-label="Breadcrumb">
            <ol class="flex items-center space-x-2">
              <li v-for="(crumb, index) in breadcrumb" :key="crumb.id" class="flex items-center">
                <template v-if="index > 0">
                  <svg class="w-5 h-5 text-gray-400 mx-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                  </svg>
                </template>
                <Link 
                  v-if="index < breadcrumb.length - 1"
                  :href="route('files', [crumb.id])"
                  class="text-sonnenglas-600 hover:text-sonnenglas-700 font-medium"
                >
                  {{ crumb.name }}
                </Link>
                <span v-else class="text-gray-900 font-medium">
                  {{ crumb.name }}
                </span>
              </li>
            </ol>
          </nav>
        </div>

        <!-- Search and Filter Section -->
        <div class="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div class="flex flex-col sm:flex-row gap-4">
            <div class="flex-1">
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                </div>
                <input
                  v-model="searchTerm"
                  type="text"
                  :placeholder="$t('Dateien durchsuchen...')"
                  class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-sonnenglas-500 focus:border-sonnenglas-500"
                />
              </div>
            </div>
            <div class="flex space-x-2">
              <button
                @click="viewMode = 'grid'"
                :class="[
                  'p-2 rounded-md transition-colors',
                  viewMode === 'grid' 
                    ? 'bg-sonnenglas-100 text-sonnenglas-700' 
                    : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                ]"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                </svg>
              </button>
              <button
                @click="viewMode = 'list'"
                :class="[
                  'p-2 rounded-md transition-colors',
                  viewMode === 'list' 
                    ? 'bg-sonnenglas-100 text-sonnenglas-700' 
                    : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                ]"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Folders Section -->
        <div v-if="filteredFolders.length > 0" class="mb-8">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">{{ $t('Ordner') }}</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Link
              v-for="folder in filteredFolders"
              :key="folder.id"
              :href="route('files', [folder.id])"
              class="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-6"
            >
              <div class="flex flex-col items-center text-center">
                <div class="w-16 h-16 bg-sonnenglas-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-sonnenglas-200 transition-colors">
                  <svg class="w-8 h-8 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                  </svg>
                </div>
                <h3 class="font-medium text-gray-900 group-hover:text-sonnenglas-700 transition-colors">
                  {{ folder.name }}
                </h3>
              </div>
            </Link>
          </div>
        </div>

        <!-- Files Section -->
        <div v-if="filteredFiles.length > 0">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold text-gray-900">{{ $t('Dateien') }}</h2>
            <span class="text-sm text-gray-600">
              {{ filteredFiles.length }} {{ $t('Datei(en)') }}
            </span>
          </div>

          <!-- Search scope indicator -->
          <p v-if="isSearchingAllFolders" class="text-sm text-sonnenglas-600 mb-4">
            {{ $t('Ergebnisse aus allen Ordnern') }}
          </p>

          <!-- Grid View -->
          <div v-if="viewMode === 'grid'" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div
              v-for="file in filteredFiles"
              :key="file.id"
              class="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group flex flex-col"
            >
              <!-- File Preview (fixed height) -->
              <div class="h-48 bg-gray-100 flex items-center justify-center p-4">
                <img
                  v-if="file.thumbnail_link"
                  :src="file.thumbnail_link"
                  :alt="file.name"
                  class="max-w-full max-h-full object-contain"
                />
                <div v-else class="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                  <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
              </div>

              <!-- File Info (pushed to bottom) -->
              <div class="p-4 flex flex-col flex-1">
                <h3 class="font-medium text-gray-900 mb-2 line-clamp-2 flex-1" :title="file.name">
                  {{ file.name }}
                </h3>
                <p class="text-sm text-gray-600 mb-3">
                  {{ formatFileSize(file.size) }}
                </p>
                <a
                  :href="route('files', [file.id])"
                  class="btn btn-sonnenglas w-full justify-center"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  {{ $t('Herunterladen') }}
                </a>
              </div>
            </div>
          </div>

          <!-- List View -->
          <div v-else class="bg-white rounded-lg shadow-sm overflow-hidden">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {{ $t('Datei') }}
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {{ $t('Größe') }}
                    </th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {{ $t('Aktionen') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="file in filteredFiles" :key="file.id" class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="flex-shrink-0 h-12 w-12">
                          <img
                            v-if="file.thumbnail_link"
                            :src="file.thumbnail_link"
                            :alt="file.name"
                            class="h-12 w-12 object-cover rounded"
                          />
                          <div v-else class="h-12 w-12 bg-gray-200 rounded flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                            </svg>
                          </div>
                        </div>
                        <div class="ml-4">
                          <div class="text-sm font-medium text-gray-900" :title="file.name">
                            {{ file.name }}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {{ formatFileSize(file.size) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <a
                        :href="route('files', [file.id])"
                        class="btn btn-sonnenglas btn-small"
                      >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                        {{ $t('Herunterladen') }}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredFiles.length === 0 && filteredFolders.length === 0" class="text-center py-12">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('Keine Dateien gefunden') }}</h3>
          <p class="mt-1 text-sm text-gray-500">
            {{ searchTerm ? $t('Keine Dateien entsprechen Ihrer Suche.') : $t('Dieser Ordner ist leer.') }}
          </p>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

interface File {
  id: string
  name: string
  size: number // Size in MB from Google Drive
  thumbnail_link?: string
  icon_link?: string
}

interface Folder {
  id: string
  name: string
  icon_link?: string
}

interface Breadcrumb {
  id: string
  name: string
}

interface Props {
  files: File[]
  folders: Folder[]
  breadcrumb: Breadcrumb[]
  currentFolderId: string | null
  allFiles?: File[]
}

const props = defineProps<Props>()

const searchTerm = ref('')
const viewMode = ref<'grid' | 'list'>('grid')

// Track whether we're showing results from all folders
const isSearchingAllFolders = computed(() => {
  if (!searchTerm.value) return false
  const localResults = props.files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  )
  return localResults.length === 0 && filteredFiles.value.length > 0
})

const filteredFolders = computed(() => {
  if (!searchTerm.value) return props.folders
  return props.folders.filter(folder =>
    folder.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  )
})

const filteredFiles = computed(() => {
  if (!searchTerm.value) return props.files

  const term = searchTerm.value.toLowerCase()

  // First search in current folder
  const localResults = props.files.filter(file =>
    file.name.toLowerCase().includes(term)
  )

  if (localResults.length > 0) return localResults

  // If no results in current folder, search across ALL folders
  if (props.allFiles) {
    return props.allFiles.filter(file =>
      file.name.toLowerCase().includes(term)
    )
  }

  return []
})

const formatFileSize = (sizeInMB: number): string => {
  if (sizeInMB === 0) return '0 MB'
  
  // The size is already in MB from Google Drive service
  return sizeInMB.toFixed(2) + ' MB'
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>